import type { Report, ReportsApi } from '@/data-sources/contract';
import { HttpError } from '@/lib/errors';
import { campaignPosts } from '../engine/campaigns';
import { CURRENT_USER, NEW_REPORT_WIDGETS, prototypeNow } from '../engine/reports';
import { allRows } from '../engine/rows';
import { simulate } from '../scenarios';
import { reportsDb } from './db';

const copy = (report: Report): Report => ({
  ...report,
  widgets: report.widgets.map((widget) => ({ ...widget })),
  ...(report.campaign
    ? {
        campaign: {
          ...report.campaign,
          keywords: [...report.campaign.keywords],
          postIds: [...report.campaign.postIds],
          urls: report.campaign.urls ? [...report.campaign.urls] : undefined,
        },
      }
    : {}),
});
function find(id: string): Report {
  const report = reportsDb.reports.find((candidate) => candidate.id === id);
  if (!report) throw new HttpError(404, `No report "${id}".`);
  return report;
}
function touch(report: Report): Report {
  report.modifiedAt = prototypeNow() + ++reportsDb.revision;
  return copy(report);
}
function uniqueId(name: string): string {
  const base =
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '') || 'report';
  const ids = new Set(reportsDb.reports.map((report) => report.id));
  if (!ids.has(base)) return base;
  let n = 2;
  while (ids.has(`${base}-${n}`)) n += 1;
  return `${base}-${n}`;
}
function nextKey(report: Report): string {
  let n = 1;
  const keys = new Set(report.widgets.map((widget) => widget.key));
  while (keys.has(`w${n}`)) n += 1;
  return `w${n}`;
}

export const mockReportsApi: ReportsApi = {
  // The mock stores any widget.
  async storableWidgets() {
    return null;
  },
  async listReports(ctx) {
    await simulate('listReports', ctx.signal, { latency: false });
    return reportsDb.reports.map(copy);
  },
  async getReport({ id }, ctx) {
    await simulate('getReport', ctx.signal, { latency: false });
    return copy(find(id));
  },
  async createReport({ name }, ctx) {
    await simulate('createReport', ctx.signal, { latency: false });
    const trimmed = name.trim();
    if (!trimmed) throw new HttpError(422, 'A report name is required.');
    const at = prototypeNow() + ++reportsDb.revision;
    const report: Report = {
      id: uniqueId(trimmed),
      name: trimmed,
      createdAt: at,
      modifiedAt: at,
      author: CURRENT_USER.name,
      authorBadge: 'gray',
      widgets: NEW_REPORT_WIDGETS.map((widget) => ({ ...widget })),
    };
    reportsDb.reports = [report, ...reportsDb.reports];
    return copy(report);
  },
  async deleteReport({ id }, ctx) {
    await simulate('deleteReport', ctx.signal, { latency: false });
    reportsDb.reports = reportsDb.reports.filter((report) => report.id !== id);
  },
  async addReportWidgets({ id, widgets }, ctx) {
    await simulate('addReportWidgets', ctx.signal, { latency: false });
    const report = find(id);
    const present = new Set(report.widgets.map((item) => item.widget));
    for (const widget of widgets) {
      if (!present.has(widget)) {
        report.widgets.push({ key: nextKey(report), widget });
        present.add(widget);
      }
    }
    return touch(report);
  },
  async removeReportWidget({ id, widgetKey }, ctx) {
    await simulate('removeReportWidget', ctx.signal, { latency: false });
    const report = find(id);
    report.widgets = report.widgets.filter((widget) => widget.key !== widgetKey);
    return touch(report);
  },
  async reorderReportWidgets({ id, widgets }, ctx) {
    await simulate('reorderReportWidgets', ctx.signal, { latency: false });
    const report = find(id);
    const current = new Set(report.widgets.map((widget) => widget.key));
    const next = new Set(widgets.map((widget) => widget.key));
    if (current.size !== next.size || [...current].some((key) => !next.has(key)))
      throw new HttpError(422, 'Reordering must preserve the report widgets.');
    report.widgets = widgets.map((widget) => ({ ...widget }));
    return touch(report);
  },
  async saveReportCampaign({ id, campaign }, ctx) {
    await simulate('saveReportCampaign', ctx.signal, { latency: false });
    const report = find(id);
    report.campaign = {
      ...campaign,
      keywords: [...campaign.keywords],
      postIds: [...campaign.postIds],
      urls: campaign.urls ? [...campaign.urls] : undefined,
    };
    return touch(report);
  },
  async searchCampaignPosts({ keywords, officialOnly }, ctx) {
    await simulate('searchCampaignPosts', ctx.signal);
    // The whole corpus, not a date-filtered slice: the modal draws no range
    // control, so a campaign's keywords are matched against everything.
    return campaignPosts(allRows, keywords, officialOnly);
  },
};
